import type { SoloMission } from "../solo/progress.js";

/**
 * A home that knows you (P5-UX-04, docs/player-experience-review.md gap 4): a returning player is
 * back in play in one tap. What the start page offers: carrying on in the game played last, and
 * today's daily in every game with what's done. Kept in this browser like solo progress.
 */

/** The game played last on this browser (solo or in a room), and when. */
export interface LastPlay {
  gameId: string;
  at: number;
}

const KEY = "obean.lastPlay";

export function rememberLastPlay(
  gameId: string,
  now = Date.now(),
  storage: Pick<Storage, "setItem"> | null = safe(),
): void {
  try {
    storage?.setItem(KEY, JSON.stringify({ gameId, at: now }));
  } catch {
    // No storage: the start page just doesn't offer to carry on.
  }
}

export function recallLastPlay(storage: Pick<Storage, "getItem"> | null = safe()): LastPlay | null {
  try {
    const raw = storage?.getItem(KEY);
    const v = raw ? (JSON.parse(raw) as Partial<LastPlay>) : null;
    return v && typeof v.gameId === "string" && typeof v.at === "number"
      ? { gameId: v.gameId, at: v.at }
      : null;
  } catch {
    return null;
  }
}

/**
 * What "Carry on" starts in a game: the first open mission of its line not won yet; when the line
 * is done, today's daily (if it isn't played yet); otherwise the last mission of the line again.
 * Null for a game with nothing to start.
 */
export function carryOn(
  list: readonly SoloMission[],
  daily: { missionId: string; played: boolean } | null,
): { kind: "next"; missionId: string; name: string } | { kind: "daily"; missionId: string } | null {
  const line = list.filter(
    (m) => !m.pack && !m.opensAfter && !m.openFromStart && m.track === undefined,
  );
  const next = line.find((m) => m.open && m.stars === undefined);
  if (next) return { kind: "next", missionId: next.id, name: next.name };
  // A track game (ADR 0027): the first open mission not won in any track.
  const inTrack = list.find((m) => m.track !== undefined && m.open && m.stars === undefined);
  if (inTrack) return { kind: "next", missionId: inTrack.id, name: inTrack.name };
  if (daily && !daily.played) return { kind: "daily", missionId: daily.missionId };
  const last = line.at(-1) ?? list.find((m) => m.open);
  return last ? { kind: "next", missionId: last.id, name: last.name } : null;
}

function safe(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}
